import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  IconButton,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Settings as GlobalVariableIcon,
} from '@mui/icons-material';

// SAP Design System
import { SAP_COLORS, SAP_TYPOGRAPHY, SAP_SPACING, SAP_SHADOWS } from '../theme/sapDesignSystem';
import { SAPIconButton, PageHeader } from '../components';

// API imports
import { globalVariableApi } from '../api/features/globalVariable/globalVariableApi';
import { fetchClients } from '../api/features/clientData/clientApi';
import { fetchJobDetails, fetchJobNames } from '../api/features/jobDetail/jobDetailApi';
import { useClientCode } from '../api/auth/ClientCodeContext';
import { useAuth } from '../api/auth/authContext';

// Types
import { GlobalVariable, GlobalVariableCreatePayload, GlobalVariableUpdatePayload } from '../api/features/globalVariable/globalVariable.types';

interface DropdownOption {
  label: string;
  value: number;
}

interface ClientOption {
  label: string;
  value: number;
  code: string;
}

interface JobOption {
  label: string;
  value: number;
  jobName: string;
}

// Constants
const ROWS_PER_PAGE = 10;

// SAP Styled Components
const SAPPageHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${SAP_COLORS.sapWhite} 0%, ${SAP_COLORS.sapGray1} 100%)`,
  borderRadius: '12px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
  marginBottom: SAP_SPACING.xl,
  padding: SAP_SPACING.lg,
}));

const SAPFilterCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${SAP_COLORS.sapWhite} 0%, ${SAP_COLORS.sapGray1} 100%)`,
  borderRadius: '12px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.card,
  marginBottom: SAP_SPACING.xl,
}));

const SAPDataCard = styled(Card)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapWhite,
  borderRadius: '8px',
  border: `1px solid ${SAP_COLORS.sapGray2}`,
  boxShadow: SAP_SHADOWS.sm,
}));

const SAPTableHeader = styled(TableCell)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapLightBlue,
  color: SAP_COLORS.sapGray7,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
  borderBottom: `2px solid ${SAP_COLORS.sapGray3}`,
  fontSize: '0.875rem',
}));

export default function GlobalVariablePage() {
  const { clientCode } = useClientCode();
  const { isAuthenticated } = useAuth();
  const isAdmin = clientCode === 'admin';

  // Main data state
  const [globalVariables, setGlobalVariables] = useState<GlobalVariable[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  // Current (UI) filter states
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobOption | null>(null);
  const [selectedVariableName, setSelectedVariableName] = useState<string>('');
  // Applied filters (snapshot used for API calls when Search is clicked)
  const [appliedClient, setAppliedClient] = useState<ClientOption | null>(null);
  const [appliedJob, setAppliedJob] = useState<JobOption | null>(null);
  const [appliedVariableName, setAppliedVariableName] = useState<string>('');

  // Dropdown options
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [jobOptions, setJobOptions] = useState<JobOption[]>([]);
  const [variableNameOptions, setVariableNameOptions] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVariable, setEditingVariable] = useState<GlobalVariable | null>(null);
  const [formData, setFormData] = useState({
    clientId: null as ClientOption | null,
    jobDetailId: null as JobOption | null,
    globalVariableName: '',
    globalVariableValue: '',
  });

  // Form dropdown options
  const [formJobOptions, setFormJobOptions] = useState<JobOption[]>([]);
  const [formVariableNameOptions, setFormVariableNameOptions] = useState<string[]>([]);

  // Load all dropdown options once on mount
  const loadDropdownOptions = async () => {
    setLoadingOptions(true);
    try {
      // Load all clients for admin users
      const clients = await fetchClients({ clientCode, firstResult: -1, maxResult: 0 });
      setClientOptions(clients.map((c: any) => ({ label: c.name, value: c.id, code: c.code })));
      
      // Load all jobs
      const jobs = await fetchJobDetails({ clientCode, firstResult: -1, maxResult: 0 });
      const jobOpts = jobs.map((job: any) => ({
        label: job[2] || 'Unknown Job',
        value: job[0],
        jobName: job[2]
      }));
      setJobOptions(jobOpts);
      setFormJobOptions(jobOpts);
      
      // Load all variable names
      const variableNames = await globalVariableApi.fetchGlobalVariableNames({ clientCode });
      setVariableNameOptions(variableNames);
      setFormVariableNameOptions(variableNames);
    } catch (error) {
      logger.error('Error loading dropdown options:', error);
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      loadDropdownOptions();
    }
  }, [isAuthenticated, isAdmin]);

  // Load global variables data only when search is triggered
  useEffect(() => {
    if (isAuthenticated && hasSearched) {
      loadGlobalVariables();
    }
  }, [isAuthenticated, hasSearched, page, appliedClient, appliedJob, appliedVariableName]);

  // Load client options on component mount
  useEffect(() => {
    fetchClients({ clientCode, firstResult: -1, maxResult: 0 })
      .then((clients) => {
        setClientOptions(clients.map((c: any) => ({ label: c.name, value: c.id, code: c.code })));
      })
      .catch(() => setClientOptions([]));
  }, [clientCode]);

  // Load job options when client is selected
  useEffect(() => {
    if (selectedClient && selectedClient.value) {
      fetchJobDetails({ clientCode, clientId: selectedClient.value, firstResult: -1, maxResult: 0 })
        .then((jobs) => setJobOptions(jobs.map((job: any) => ({ label: job[2] || 'Unknown Job', value: job[0], jobName: job[2] }))))
        .catch(() => setJobOptions([]));
      setSelectedJob(null);
    } else {
      setJobOptions([]);
      setSelectedJob(null);
    }
  }, [selectedClient, clientCode]);

  // Load jobs for specific client
  const loadJobsForClient = async (clientId: number) => {
    try {
      const jobs = await fetchJobDetails({ 
        clientCode, 
        firstResult: -1, 
        maxResult: 0,
        clientId 
      });
      const jobOpts = jobs.map((job: any) => ({
        label: job[2] || 'Unknown Job',
        value: job[0],
        jobName: job[2]
      }));
      setJobOptions(jobOpts);
      setFormJobOptions(jobOpts);
    } catch (error) {
      logger.error('Error loading jobs for client:', error);
      setJobOptions([]);
      setFormJobOptions([]);
    }
  };

  // Load variable names for specific client and job
  const loadVariableNamesForClientAndJob = async (clientId?: number, jobDetailId?: number) => {
    try {
      const params: any = { clientCode };
      if (clientId) params.clientId = clientId;
      if (jobDetailId) params.jobDetailId = jobDetailId;
      
      const variableNames = await globalVariableApi.fetchGlobalVariableNames(params);
      setVariableNameOptions(variableNames);
      setFormVariableNameOptions(variableNames);
    } catch (error) {
      logger.error('Error loading variable names:', error);
      setVariableNameOptions([]);
      setFormVariableNameOptions([]);
    }
  };

  // Load global variables data
  const loadGlobalVariables = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params: any = {
        clientCode,
        firstResult: page * ROWS_PER_PAGE,
        maxResult: ROWS_PER_PAGE,
        searched: true
      };

      if (appliedClient?.value) {
        params.clientId = appliedClient.value;
      }
      if (appliedJob?.value) {
        params.jobDetailId = appliedJob.value;
      }
      if (appliedVariableName) {
        params.globalVariableName = appliedVariableName;
      }

      const [data, count] = await Promise.all([
        globalVariableApi.fetch(params),
        globalVariableApi.count(params),
      ]);

      setGlobalVariables(data);
      setTotalCount(count);
    } catch (err: any) {
      setError('Failed to load global variables');
      setGlobalVariables([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle client change
  const handleClientChange = (event: any, newValue: ClientOption | null) => {
    // If user clears the client combobox (clicks X), behave like full reset
    if (!newValue) {
      handleReset();
      return;
    }
    setSelectedClient(newValue);
  };

  // Handle job change
  const handleJobChange = async (event: any, newValue: JobOption | null) => {
    // If user clears the job combobox (clicks X), behave like full reset
    if (!newValue) {
      handleReset();
      return;
    }
    setSelectedJob(newValue);
    setSelectedVariableName('');
    setPage(0);
    
    if (newValue && selectedClient?.value) {
      await loadVariableNamesForClientAndJob(selectedClient.value, newValue.value);
    }
  };

  // Handle variable name change
  const handleVariableNameChange = (event: any, newValue: string | null) => {
    // If user clears the variable-name combobox (clicks X), behave like full reset
    if (!newValue) {
      handleReset();
      return;
    }
    setSelectedVariableName(newValue || '');
    setPage(0);
  };

  // Handle search
  const handleSearch = () => {
    // Take a snapshot of current filters and use those for API calls
    setAppliedClient(selectedClient);
    setAppliedJob(selectedJob);
    setAppliedVariableName(selectedVariableName);
    setPage(0);
    setHasSearched(true);
  };

  // Handle reset
  const handleReset = () => {
    setSelectedClient(null);
    setSelectedJob(null);
    setSelectedVariableName('');
    setAppliedClient(null);
    setAppliedJob(null);
    setAppliedVariableName('');
    setPage(0);
    setHasSearched(false);
    setGlobalVariables([]);
    setTotalCount(0);
    setError(null);
  };

  // Handle page change
  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage);
  };

  // Handle edit
  const handleEdit = (variable: GlobalVariable) => {
    setEditingVariable(variable);
    const selectedClient = clientOptions.find(c => c.value === variable.clientId);
    const selectedJob = jobOptions.find(j => j.value === variable.jobDetailId);
    
    setFormData({
      clientId: selectedClient || null,
      jobDetailId: selectedJob || null,
      globalVariableName: variable.globalVariableName || '',
      globalVariableValue: variable.globalVariableValue || '',
    });
    setModalOpen(true);
  };

  // Handle create new
  const handleCreate = () => {
    setEditingVariable(null);
    setFormData({
      clientId: null,
      jobDetailId: null,
      globalVariableName: '',
      globalVariableValue: '',
    });
    setModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalOpen(false);
    setEditingVariable(null);
    setFormData({
      clientId: null,
      jobDetailId: null,
      globalVariableName: '',
      globalVariableValue: '',
    });
  };

  // Handle form submit
  const handleFormSubmit = async () => {
    if (!formData.clientId || !formData.jobDetailId || !formData.globalVariableName || !formData.globalVariableValue) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingVariable) {
        const payload: GlobalVariableUpdatePayload = {
          id: editingVariable.globalId,
          clientId: formData.clientId.value,
          jobDetailId: formData.jobDetailId.value,
          globalVariableName: formData.globalVariableName,
          globalVariableValue: formData.globalVariableValue,
        };
        await globalVariableApi.update({ clientCode, payload });
        toast.success('Global variable updated successfully');
      } else {
        const payload: GlobalVariableCreatePayload = {
          clientId: formData.clientId.value,
          jobDetailId: formData.jobDetailId.value,
          globalVariableName: formData.globalVariableName,
          globalVariableValue: formData.globalVariableValue,
        };
        await globalVariableApi.create({ clientCode, payload });
        toast.success('Global variable created successfully');
      }

      handleModalClose();
      loadGlobalVariables();
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save global variable');
      logger.error('Error saving global variable:', err);
    }
  };

  // Handle form client change
  const handleFormClientChange = async (event: any, newValue: ClientOption | null) => {
    setFormData(prev => ({ ...prev, clientId: newValue, jobDetailId: null }));
    
    if (newValue) {
      await loadJobsForClient(newValue.value);
      await loadVariableNamesForClientAndJob(newValue.value);
    } else {
      // Load all jobs and variable names when no client is selected
      const jobs = await fetchJobDetails({ clientCode, firstResult: -1, maxResult: 0 });
      const jobOpts = jobs.map((job: any) => ({
        label: job[2] || 'Unknown Job',
        value: job[0],
        jobName: job[2]
      }));
      setFormJobOptions(jobOpts);
      
      const variableNames = await globalVariableApi.fetchGlobalVariableNames({ clientCode });
      setFormVariableNameOptions(variableNames);
    }
  };

  // Handle form job change
  const handleFormJobChange = async (event: any, newValue: JobOption | null) => {
    setFormData(prev => ({ ...prev, jobDetailId: newValue }));
    
    if (newValue && formData.clientId?.value) {
      await loadVariableNamesForClientAndJob(formData.clientId.value, newValue.value);
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Box sx={{ p: SAP_SPACING.lg, bgcolor: SAP_COLORS.sapGray1, minHeight: '100vh' }}>
      {/* Page Header */}
      <PageHeader 
        title="Global Variable Management"
        description="Manage global variables across clients and jobs with centralized configuration"
      />

      {/* Search and Filter Card */}
      <SAPFilterCard>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            {isAdmin && (
              <Box sx={{ minWidth: { xs: '100%', sm: 200 }, flex: 1 }}>
                <Autocomplete
                  options={clientOptions}
                  getOptionLabel={(option) => option.label}
                  value={selectedClient}
                  onChange={handleClientChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Client" />
                  )}
                />
              </Box>
            )}

            <Box sx={{ minWidth: { xs: '100%', sm: 200 }, flex: 1 }}>
              <Autocomplete
                options={jobOptions}
                getOptionLabel={(option) => option.label}
                value={selectedJob}
                onChange={handleJobChange}
                disabled={!selectedClient}
                renderInput={(params) => (
                  <TextField {...params} label="Job Detail" />
                )}
              />
            </Box>

            <Box sx={{ minWidth: { xs: '100%', sm: 200 }, flex: 1 }}>
              <Autocomplete
                options={variableNameOptions}
                getOptionLabel={(option) => option}
                value={selectedVariableName}
                onChange={handleVariableNameChange}
                disabled={loading || loadingOptions}
                renderInput={(params) => (
                  <TextField {...params} label="Variable Name" />
                )}
                isOptionEqualToValue={(option, value) => option === value}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <SAPIconButton
                icon="search"
                onClick={handleSearch}
                variant="primary"
                loading={loading}
              />
              <SAPIconButton
                icon="reset"
                onClick={handleReset}
                variant="secondary"
              />
              <SAPIconButton
              icon="add"
              variant="success"
              onClick={handleCreate}
              tooltip="Create New Global Variable"
            />
            </Box>
          </Box>
        </CardContent>
      </SAPFilterCard>

      {/* Data Table Card */}
      <SAPDataCard>
        <CardContent sx={{ p: 0 }}>
          {error && (
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : !hasSearched ? (
            <Box sx={{ 
              textAlign: 'center', 
              p: 4 
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: SAP_COLORS.sapGray5,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                  mb: 2,
                }}
              >
                No data to display
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: SAP_COLORS.sapGray4,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                }}
              >
                Use the filters above and click Search to load global variables
              </Typography>
            </Box>
          ) : globalVariables.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              p: 4 
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: SAP_COLORS.sapGray5,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
                  mb: 2,
                }}
              >
                No global variables found
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: SAP_COLORS.sapGray4,
                  fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
                }}
              >
                Try adjusting your search criteria or click Search again
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <SAPTableHeader>S.No.</SAPTableHeader>
                      <SAPTableHeader>Client</SAPTableHeader>
                      <SAPTableHeader>Job Detail</SAPTableHeader>
                      <SAPTableHeader>Variable Name</SAPTableHeader>
                      <SAPTableHeader>Variable Value</SAPTableHeader>
                      <SAPTableHeader>Actions</SAPTableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {globalVariables.map((variable, index) => {
                      const client = clientOptions.find(c => c.value === variable.clientId);
                      const job = jobOptions.find(c => c.value === variable.jobDetailId);
                      
                      return (
                        <TableRow key={variable.globalId} hover>
                          <TableCell>{page * ROWS_PER_PAGE + index + 1}</TableCell>
                          <TableCell>{client?.label || 'Unknown'}</TableCell>
                          <TableCell>{job?.label || 'Unknown'}</TableCell>
                          <TableCell>{variable.globalVariableName}</TableCell>
                          <TableCell>
                            <Box sx={{ maxWidth: 200, wordBreak: 'break-word' }}>
                              {variable.globalVariableValue}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(variable)}
                              sx={{ color: SAP_COLORS.sapBlue }}
                            >
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {globalVariables.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  {/* Pagination Info */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Showing {(page * ROWS_PER_PAGE) + 1}-{Math.min((page * ROWS_PER_PAGE) + globalVariables.length, totalCount)} of {totalCount} global variables
                    </Typography>
                  </Box>
                  
                  {/* Pagination Controls */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Rows per page</InputLabel>
                      <Select
                        value={ROWS_PER_PAGE}
                        onChange={(event: SelectChangeEvent<number>) => {
                          // Note: ROWS_PER_PAGE is constant, so this is just for UI consistency
                        }}
                        label="Rows per page"
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Pagination
                      count={Math.ceil(totalCount / ROWS_PER_PAGE)}
                      page={page + 1}
                      onChange={(event: React.ChangeEvent<unknown>, newPage: number) => {
                        handlePageChange(event, newPage - 1);
                      }}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </SAPDataCard>

      {/* Create/Edit Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: SAP_SHADOWS.lg,
            maxHeight: '90vh',
            m: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: SAP_COLORS.sapGray1,
          borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
          color: SAP_COLORS.sapGray7,
          fontWeight: SAP_TYPOGRAPHY.fontWeight.bold,
          py: 2,
          px: { xs: 2, sm: 3 },
        }}>
          {editingVariable ? 'Edit Global Variable' : 'Create New Global Variable'}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            flex: '1 1 auto',
            overflowY: 'auto',
            bgcolor: SAP_COLORS.sapWhite,
            px: { xs: 2, sm: 3 },
            py: 3,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: { xs: 2, sm: 3 },
              rowGap: 2.5,
              alignItems: 'flex-start',
            }}
          >
            {/* Client */}
            {isAdmin && (
              editingVariable ? (
                <TextField
                  label="Client"
                  value={editingVariable.clientName}
                  size="small"
                  fullWidth
                  disabled
                />
              ) : (
                <Autocomplete
                  options={clientOptions}
                  value={formData.clientId}
                  onChange={handleFormClientChange}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Client *"
                      size="small"
                      fullWidth
                    />
                  )}
                  loading={loadingOptions}
                  disabled={loadingOptions}
                />
              )
            )}

            {/* Job Detail */}
            {editingVariable ? (
              <TextField
                label="Job Detail"
                value={editingVariable.jobName}
                size="small"
                fullWidth
                disabled
              />
            ) : (
              <Autocomplete
                options={formJobOptions}
                value={formData.jobDetailId}
                onChange={handleFormJobChange}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Job Detail *"
                    size="small"
                    fullWidth
                  />
                )}
                loading={loadingOptions}
                disabled={loadingOptions}
              />
            )}

            {/* Variable Name */}
            {editingVariable ? (
              <TextField
                label="Variable Name"
                size="small"
                value={formData.globalVariableName}
                fullWidth
                disabled
                helperText="Variable name cannot be changed when editing"
              />
            ) : (
              <TextField
                label="Variable Name *"
                size="small"
                value={formData.globalVariableName}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, globalVariableName: e.target.value }))
                }
                placeholder="Enter variable name"
                fullWidth
              />
            )}

            {/* Variable Value */}
            <TextField
              label="Variable Value *"
              multiline
              rows={3}
              value={formData.globalVariableValue}
              onChange={(e) => setFormData(prev => ({ ...prev, globalVariableValue: e.target.value }))}
              size="small"
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            borderTop: `1px solid ${SAP_COLORS.sapGray2}`,
            gap: 1.5,
          }}
        >
          <Button
            onClick={handleModalClose}
            sx={{
              borderColor: SAP_COLORS.sapGray3,
              color: SAP_COLORS.sapGray7,
              '&:hover': { borderColor: SAP_COLORS.sapGray5 },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleFormSubmit}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: SAP_COLORS.sapBlue,
              '&:hover': { backgroundColor: SAP_COLORS.sapDarkBlue },
            }}
          >
            {editingVariable ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}